import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import {
  ConfirmationService,
  Message,
  MessageService,
  PrimeIcons
} from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import {
  CompositionControllerService,
  CompositionDetails,
  Edition,
  EditionControllerService,
  PageableRequest
} from 'src/app/admin-api';
import {
  DropdownFilter,
  PageContent,
  TableActionButton,
  TableColumn,
  TableComponent
} from 'src/app/components/table';
import { TableModalComponent } from 'src/app/components/table-modal/table-modal.component';
import {
  CompositionDTO,
  CompositionStatusCounter,
  EditionId,
  Role,
  SubscriptionId,
  getAllSubscriptionsIncludeAll,
  roleAsObject
} from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';
import {
  CompositionDashboardPage,
  CompositionDashboardStatusGroup,
  EditionsDashboardClient,
  mapDashboardRow,
  sameFieldList,
  sortDashboardStatusGroups,
  statusGroupFields
} from './editions-dashboard.client';
import { PendingPreparationComponent } from './pending-preparation/pending-preparation.component';

@Component({
  selector: 'app-editions-dashboard',
  templateUrl: './editions-dashboard.component.html',
  styleUrl: './editions-dashboard.component.scss',
  providers: [DatePipe, ConfirmationService, MessageService, DialogService]
})
export class EditionsDashboardComponent implements OnInit, OnDestroy {
  @ViewChild('compositionsTable')
  compositionsTable: TableComponent | undefined;

  private compositionService: CompositionControllerService = inject(
    CompositionControllerService
  );
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private title: Title = inject(Title);
  private editionService: EditionControllerService = inject(
    EditionControllerService
  );
  private datePipe: DatePipe = inject(DatePipe);
  private confirmationService: ConfirmationService =
    inject(ConfirmationService);
  private messageService: MessageService = inject(MessageService);
  private dialogService: DialogService = inject(DialogService);
  private ngxPermissionService: NgxPermissionsService = inject(
    NgxPermissionsService
  );
  dashboardClient = inject(EditionsDashboardClient);

  form = new FormGroup({
    subscriptionId: new FormControl<number>(1, [
      Validators.required,
      Validators.min(0)
    ]),
    editionId: new FormControl<number>(
      Number(
        `${new Date().getFullYear()}${(new Date().getMonth() + 1)
          .toString()
          .padStart(2, '0')}`
      ),
      [Validators.required, Validators.min(1)]
    )
  });
  pendingOnly = new FormControl<boolean>(false, { nonNullable: true });
  subscriptions = getAllSubscriptionsIncludeAll().filter(
    (s) => !SubscriptionId.isSubscription(s.value)
  );
  editions: Array<Edition> | undefined;
  subscriberEditionStatusList: Array<CompositionStatusCounter> | undefined;
  compositions: Array<CompositionDTO> | undefined;
  cols: Array<TableColumn> | undefined;
  ready = false;
  totalSubscribersCount = 0;
  awaitingAllocation = 0;
  exportCount = 0;
  exportLabel = 'Exportar(0)';
  exporting = false;
  private milleniumFields: string[] = [];
  private exportPoll?: ReturnType<typeof setInterval>;
  actionButtons: Array<TableActionButton> = [
    new TableActionButton(
      '',
      'prepare',
      PrimeIcons.PLAY,
      (item: CompositionDTO) => item.pending > 0,
      'p-button-success',
      'Preparar composição',
      'bottom'
    ),
    new TableActionButton(
      '',
      'delete',
      PrimeIcons.TRASH,
      (item: CompositionDTO) => item.pending > 0,
      '',
      'Excluir composição',
      'bottom',
      true,
      true,
      'danger'
    )
  ];
  permission = false;
  pendingPreparation: CompositionDetails[];

  rowSelectable = (item: { data: CompositionDTO }): boolean =>
    item.data.pending > 0;

  get selectedCount(): number {
    return this.compositionsTable?.selectedItems?.length || 0;
  }

  get statusColspan(): number {
    return (this.form.value.subscriptionId ? 4 : 5) + 1;
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(async (params) => {
      LoaderService.showLoader();
      this.permission = await this.ngxPermissionService.hasPermission([
        roleAsObject(Role.Full_Administrator).enumValue,
        roleAsObject(Role.Logistics).enumValue
      ]);
      if (params['editionId']) {
        this.ready = false;
        this.form.patchValue({
          editionId: Number(params['editionId']),
          subscriptionId: EditionId.subscriptionId(Number(params['editionId']))
        });
        const fail = document.getElementById('img-fail');
        if (fail?.style?.display === 'none') {
          fail.style.display = 'block';
        }
        await Promise.all([this.findEditions(), this.findPendingPreparation()]);
        try {
          await this.refreshExportCount();
        } catch (error) {
          this.exportCount = 0;
          this.exportLabel = 'Exportar(0)';
          AppDialogService.showErrorDialog(error);
        }
        try {
          await this.loadDashboardMeta();
          void this.refreshAggregates();
        } catch (error) {
          AppDialogService.showErrorDialog(error);
        }
        this.title.setTitle('Dashboard de Edições - ' + this.edition?.theme);
        LoaderService.showLoader(false);
      } else {
        this.navigate();
      }
    });
  }

  navigate(): void {
    this.router.navigate(['/operations/editions/' + this.form.value.editionId]);
  }

  async findPendingPreparation(): Promise<void> {
    try {
      this.pendingPreparation = await lastValueFrom(
        this.compositionService
          .findPendingPreparation()
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  ngOnDestroy(): void {
    this.stopExportPoll();
  }

  findPage = async (
    request: PageableRequest
  ): Promise<PageContent<CompositionDTO>> => {
    const page = await lastValueFrom(
      this.dashboardClient.findDashboardPage(
        this.form.value.editionId,
        request.page || 0,
        request.pageSize || 50,
        this.pendingOnly.value,
        false,
        request.sortBy,
        request.sortDirection
      )
    );
    return {
      ...page.compositions,
      content: (page.compositions?.content || []).map(mapDashboardRow)
    };
  };

  private async refreshAggregates(): Promise<void> {
    try {
      const page = await lastValueFrom(
        this.dashboardClient.findDashboardPage(
          this.form.value.editionId,
          0,
          1,
          false,
          true
        )
      );
      this.applyDashboardMeta(page);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async loadDashboardMeta(): Promise<void> {
    this.subscriberEditionStatusList = undefined;
    this.milleniumFields = [];
    this.resetCols([]);
    this.ready = true;
  }

  async onPendingOnlyChange(): Promise<void> {
    if (!this.compositionsTable) {
      return;
    }
    this.compositionsTable.pageRequest = {
      ...(this.compositionsTable.pageRequest || {
        page: 0,
        pageSize: 50
      }),
      page: 0
    };
    await this.compositionsTable.pageTable();
  }

  private applyDashboardMeta(page: CompositionDashboardPage): void {
    if (page.totalSubscribers != null) {
      this.totalSubscribersCount = page.totalSubscribers || 0;
      this.awaitingAllocation = page.awaitingAllocation || 0;
    }
    if (!page.statusGroups?.length) {
      return;
    }
    const statusGroups = sortDashboardStatusGroups(page.statusGroups);
    this.subscriberEditionStatusList = statusGroups.map((group) => ({
      id: group.id,
      status: group.status,
      total: group.total,
      pending: group.pending,
      statusList: group.statusList || []
    }));
    const fields = statusGroupFields(statusGroups);
    if (sameFieldList(fields, this.milleniumFields) && this.cols?.length) {
      return;
    }
    this.milleniumFields = fields;
    this.resetCols(this.buildStatusColumns(statusGroups));
  }

  private buildStatusColumns(
    groups: CompositionDashboardStatusGroup[]
  ): Array<TableColumn> {
    return groups.reduce((list: Array<TableColumn>, group) => {
      return list.concat(
        (group.statusList || []).map((cell) =>
          new TableColumn.Builder()
            .setHeader(`${cell.status} (${cell.total})`)
            .setField(`${cell.status}_${group.id}`)
            .setFilter(false)
            .setType('formattedInteger')
            .build()
        )
      );
    }, []);
  }

  async findEditions(): Promise<void> {
    try {
      this.editions = await lastValueFrom(
        this.editionService
          .findEditionsBySubscriptionId(this.form.value.subscriptionId || 1)
          .pipe(map((data) => data.result))
      );
      if (this.form.value.subscriptionId === 0) {
        this.editions?.forEach(
          (e) => (e.editionId = EditionId.yearMonthReference(e.editionId || 0))
        );
      }
    } catch (error: any) {
      this.editions = [];
    }
  }

  async changeSubscription(): Promise<void> {
    LoaderService.showLoader();
    const form = { ...this.form.value };
    this.form.controls['editionId'].reset(
      Number(
        (form.subscriptionId || 1).toString() +
          EditionId.yearMonthReference(form.editionId)
      )
    );
    this.navigate();
    LoaderService.showLoader(false);
  }

  async changeEdition(): Promise<void> {
    LoaderService.showLoader();
    this.navigate();
    LoaderService.showLoader(false);
  }

  editionDate(editionId: number): string {
    return FormUtil.editionDate(editionId);
  }

  editionDateName(editionId: number): string {
    return (
      this.datePipe.transform(
        new Date(
          `${EditionId.year(editionId)}-${EditionId.month(editionId).toString().padStart(2, '0')}-02`
        ),
        'MMMM/yyyy'
      ) || this.editionDate(editionId)
    );
  }

  subscriberStatusColor(subscriberStatus: number): string {
    return FormUtil.subscriberStatusColor(subscriberStatus);
  }

  resetCols(concat?: Array<TableColumn>): void {
    const checkboxCol = new TableColumn.Builder()
      .setHeader('')
      .setField('')
      .setType('checkbox')
      .setSelectDisabledTooltip('Composição já preparada')
      .build();
    let orig = [
      checkboxCol,
      new TableColumn(
        'Id',
        'compositionId',
        false,
        'number',
        '/operations/compositions/id/',
        'compositionId'
      ),
      new TableColumn(
        'Composição',
        'compositionName',
        false,
        'text',
        '/operations/compositions/id/',
        'compositionId'
      ),
      new TableColumn('Total', 'total', false, 'number'),
      new TableColumn('Pendente preparo', 'pending', false, 'number')
    ];
    if (!this.form.value.subscriptionId) {
      orig = [
        checkboxCol,
        new TableColumn(
          'Id',
          'compositionId',
          true,
          'number',
          '/operations/compositions/id/',
          'compositionId'
        ),
        new TableColumn('Assinatura', 'subscriptionName', true, 'text'),
        new TableColumn(
          'Composição',
          'compositionName',
          true,
          'text',
          '/operations/compositions/id/',
          'compositionId',
          true,
          'contains'
        ),
        new TableColumn('Total', 'total', false, 'formattedInteger'),
        new TableColumn(
          'Pendente preparo',
          'pending',
          false,
          'formattedInteger'
        )
      ];
    }
    this.cols = orig
      .concat(concat)
      .concat(
        this.permission
          ? [new TableColumn('Ação', '', false, 'button')]
          : [new TableColumn('Ação', '', false, 'button')]
      );
  }

  callAction($event: {
    item: CompositionDTO;
    $event: Event;
    action: string;
  }): void {
    if ($event.action === 'prepare') {
      this.confirmPrepareComposition($event);
    } else if ($event.action === 'delete') {
      this.confirmDeleteComposition($event);
    }
  }

  confirmPrepareComposition($event: {
    item: CompositionDTO;
    $event: Event;
    action: string;
  }): void {
    this.confirmationService.confirm({
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      target: $event.$event.target,
      message: `Deseja preparar a composição '${$event.item.compositionName}'?`,
      header: 'Preparar composição',
      accept: async () => {
        await this.prepareComposition($event.item);
      }
    });
  }

  async prepareComposition(composition: CompositionDTO) {
    try {
      LoaderService.showLoader();
      const result = await prepareComposition(
        composition,
        this.compositionService
      );
      composition = result.composition;
      this.messageService.add(result.message);
      await this.findPendingPreparation();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  confirmDeleteComposition($event: {
    item: CompositionDTO;
    $event: Event;
    action: string;
  }): void {
    this.confirmationService.confirm({
      acceptButtonStyleClass: 'p-button-danger',
      acceptIcon: PrimeIcons.TRASH,
      acceptLabel: 'Excluir',
      rejectButtonStyleClass: 'p-button-primary',
      rejectIcon: PrimeIcons.ARROW_LEFT,
      rejectLabel: 'Voltar',
      target: $event.$event.target,
      message: `Tem certeza que deseja excluir a composição '${$event.item.compositionName}'?`,
      header: 'Excluir composição',
      accept: async () => {
        await this.deleteComposition($event.item);
      }
    });
  }

  async deleteComposition(composition: CompositionDTO) {
    LoaderService.showLoader();
    try {
      const detail = await lastValueFrom(
        this.compositionService
          .deleteComposition(composition.compositionId)
          .pipe(map((data) => data.result))
      );
      this.messageService.add({
        detail,
        summary: 'Sucesso',
        severity: 'success'
      });
      await this.reloadTable();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  confirmPrepareSelected(): void {
    const selected = this.getSelectedCompositions();
    if (!selected.length) return;
    this.confirmationService.confirm({
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      message: `Deseja preparar ${selected.length} composição(ões) selecionada(s)?`,
      header: 'Preparar composições',
      accept: async () => {
        await this.prepareSelectedCompositions(selected);
      }
    });
  }

  confirmDeleteSelected(): void {
    const selected = this.getSelectedCompositions();
    if (!selected.length) return;
    this.confirmationService.confirm({
      acceptButtonStyleClass: 'p-button-danger',
      acceptIcon: PrimeIcons.TRASH,
      acceptLabel: 'Excluir',
      rejectButtonStyleClass: 'p-button-primary',
      rejectIcon: PrimeIcons.ARROW_LEFT,
      rejectLabel: 'Voltar',
      message: `Tem certeza que deseja excluir ${selected.length} composição(ões) selecionada(s)?`,
      header: 'Excluir composições',
      accept: async () => {
        await this.deleteSelectedCompositions(selected);
      }
    });
  }

  async prepareSelectedCompositions(compositions: CompositionDTO[]) {
    LoaderService.showLoader();
    try {
      const result = await lastValueFrom(
        this.dashboardClient.prepareBatch(
          compositions.map((composition) => composition.compositionId)
        )
      );
      const details: string[] = [];
      if (result.success) {
        details.push(`${result.success} preparada(s) com sucesso`);
      }
      if (result.error) {
        details.push(`${result.error} com erro`);
      }
      const firstError = result.items?.find((item) => item.error)?.error;
      this.messageService.add({
        severity:
          result.error && !result.success
            ? 'error'
            : result.error
              ? 'warn'
              : 'success',
        summary: 'Preparo em lote',
        detail: [details.join('; '), firstError].filter(Boolean).join('. ')
      });
      await this.reloadTable();
      this.clearTableSelection();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async deleteSelectedCompositions(compositions: CompositionDTO[]) {
    LoaderService.showLoader();
    try {
      let successCount = 0;
      let errorCount = 0;
      for (const composition of compositions) {
        try {
          await lastValueFrom(
            this.compositionService.deleteComposition(composition.compositionId)
          );
          successCount++;
        } catch {
          errorCount++;
        }
      }
      this.messageService.add({
        severity: errorCount && !successCount ? 'error' : 'success',
        summary: 'Exclusão em lote',
        detail: `${successCount} excluída(s)${
          errorCount ? `; ${errorCount} com erro` : ''
        }.`
      });
      await this.reloadTable();
      this.clearTableSelection();
    } finally {
      LoaderService.showLoader(false);
    }
  }

  getSelectedCompositions(): CompositionDTO[] {
    return (this.compositionsTable?.selectedItems as CompositionDTO[]) || [];
  }

  clearTableSelection(): void {
    if (!this.compositionsTable) return;
    this.compositionsTable.selectedItems = [];
    this.compositionsTable.checkboxAll = false;
  }

  async findSubscribers(
    statusId: number,
    status: string = null
  ): Promise<void> {
    LoaderService.showLoader();
    try {
      const subscribers = await lastValueFrom(
        this.compositionService
          .findSubscribersBySubscriberEditionStatusAndEditionId(
            statusId,
            this.form.value.editionId
          )
          .pipe(map((data) => data.result))
      );
      this.dialogService.open(TableModalComponent, {
        width: '80%',
        height: '90vh',
        header: status
          ? 'Assinantes com status ' + status
          : 'Assinantes da edição ' + this.edition?.theme,
        maximizable: true,
        data: {
          cols: this.subscriberColumns(statusId),
          elements: subscribers,
          modelName: 'assinantes',
          dropdownFilters: {
            subscriberEditionStatusName: subscribers.reduce(
              (list: Array<DropdownFilter>, s) => {
                if (
                  !list.some((ss) => ss.value === s.subscriberEditionStatusName)
                ) {
                  list.push({
                    label: s.subscriberEditionStatusName,
                    value: s.subscriberEditionStatusName
                  });
                }
                list.sort((c1, c2) => {
                  if (c1.value > c2.value) return 1;
                  if (c1.value < c2.value) return -1;
                  return 0;
                });
                return list;
              },
              []
            ),
            statusMillenium: subscribers.reduce(
              (list: Array<DropdownFilter>, s) => {
                if (!list.some((ss) => ss.value === s.statusMillenium)) {
                  list.push({
                    label: s.statusMillenium,
                    value: s.statusMillenium
                  });
                }
                list.sort((c1, c2) => {
                  if (c1.value > c2.value) return 1;
                  if (c1.value < c2.value) return -1;
                  return 0;
                });
                return list;
              },
              []
            )
          }
        }
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
    LoaderService.showLoader(false);
  }

  showPending() {
    this.dialogService
      .open(PendingPreparationComponent, {
        data: {
          pending: this.pendingPreparation
        },
        header: 'Composições aguardando preparo',
        maximizable: true
      })
      .onClose.subscribe(async () => {
        LoaderService.showLoader();
        await this.findPendingPreparation();
        LoaderService.showLoader(false);
      });
  }

  subscriberColumns(statusId: number): Array<TableColumn> {
    let cols: Array<TableColumn>;
    if (!this.form.value.subscriptionId) {
      cols = [
        new TableColumn(
          'Id',
          'subscriberId',
          true,
          'number',
          '/users/subscribers/',
          'subscriberId'
        ),
        new TableColumn(
          'Assinatura',
          'subscriptionName',
          true,
          'text',
          undefined,
          undefined,
          true,
          'contains'
        ),
        new TableColumn(
          'GlamId',
          'boxId',
          true,
          'text',
          '/users/subscribers/',
          'subscriberId'
        ),
        new TableColumn(
          'Assinante',
          'personName',
          true,
          'text',
          '/users/person/',
          'personId',
          true,
          'contains'
        ),
        new TableColumn(
          'Status ',
          'subscriberEditionStatusName',
          true,
          'text',
          undefined,
          undefined,
          true,
          'contains'
        )
      ];
    } else {
      cols = [
        new TableColumn(
          'Id',
          'subscriberId',
          true,
          'number',
          '/users/subscribers/',
          'subscriberId'
        ),
        new TableColumn(
          'GlamId',
          'boxId',
          true,
          'text',
          '/users/subscribers/',
          'subscriberId'
        ),
        new TableColumn(
          'Assinante',
          'personName',
          true,
          'text',
          '/users/person/',
          'personId',
          true,
          'contains'
        )
      ];
    }
    if (statusId) {
      cols = cols.concat([
        new TableColumn(
          'Status Millennium',
          'statusMillenium',
          true,
          'text',
          undefined,
          undefined,
          true,
          'contains'
        ),
        new TableColumn(
          'Últ atualização',
          'dateUpdatedMillenium',
          true,
          'date',
          undefined,
          undefined,
          true,
          'between'
        )
      ]);
    }
    return cols;
  }

  get pending(): number {
    return this.awaitingAllocation;
  }

  get editionImage(): string {
    return FormUtil.editionImage(this.edition?.editionId || 0);
  }

  get edition(): Edition | undefined {
    return this.editions?.find(
      (e) => e.editionId === this.form.value.editionId
    );
  }

  get totalSubscribers(): number {
    return this.totalSubscribersCount;
  }

  confirmExportErp(): void {
    if (this.exportCount < 1 || this.exporting) {
      return;
    }
    this.confirmationService.confirm({
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      message: `Deseja disparar a exportação de ${this.exportCount} pedido(s) de edição para o ERP?`,
      header: 'Exportar pedidos de edição',
      accept: async () => {
        await this.exportErp();
      }
    });
  }

  async refreshExportCount(): Promise<void> {
    const result = await lastValueFrom(
      this.dashboardClient.countExportErp(this.form.value.editionId)
    );
    this.exportCount = result.count || 0;
    this.exportLabel = `Exportar(${this.exportCount})`;
  }

  async exportErp(): Promise<void> {
    LoaderService.showLoader();
    try {
      const detail = await lastValueFrom(
        this.dashboardClient.exportErp(this.form.value.editionId)
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Exportação disparada',
        detail
      });
      this.startExportPoll();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private startExportPoll(): void {
    this.stopExportPoll();
    this.exporting = true;
    let ticks = 0;
    this.exportPoll = setInterval(() => {
      ticks += 1;
      this.refreshExportCount()
        .then(() => {
          if (this.exportCount === 0 || ticks >= 12) {
            this.exporting = false;
            this.stopExportPoll();
          }
        })
        .catch((error) => {
          this.exporting = false;
          this.stopExportPoll();
          AppDialogService.showErrorDialog(error);
        });
    }, 10000);
  }

  private stopExportPoll(): void {
    if (this.exportPoll) {
      clearInterval(this.exportPoll);
      this.exportPoll = undefined;
    }
  }

  private async reloadTable(): Promise<void> {
    await Promise.all([
      this.compositionsTable
        ? this.compositionsTable.pageTable()
        : this.loadDashboardMeta(),
      this.findPendingPreparation()
    ]);
    try {
      await this.refreshExportCount();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  get oldEdition() {
    return !EditionId.subscriptionId(this.form.value.editionId);
  }
}

export async function prepareComposition(
  composition: CompositionDTO,
  compositionService: CompositionControllerService
): Promise<{ composition: CompositionDTO; message: Message }> {
  const result = await lastValueFrom(
    compositionService
      .prepareComposition(composition.compositionId)
      .pipe(map((data) => data.result))
  );
  let message: Message;
  if (result.pending && result.pending <= composition.pending) {
    message = {
      severity: 'warn',
      detail: `${result.pending} ${
        result.pending > 1
          ? ' edições não puderem ser preparadas'
          : ' edição não pode ser preparada'
      } .`,
      summary: 'Composição preparada parcialmente'
    };
  } else if (!result.pending) {
    message = {
      severity: 'success',
      detail: 'Composição preparada com sucesso.',
      summary: 'Composição preparada'
    };
  } else {
    message = {
      severity: 'error',
      detail:
        'Não foi possível preparar a composição. Verifique as assinantes.',
      summary: 'Composição não preparada'
    };
  }
  composition.pending = result.pending;
  return { composition, message };
}
